/**
 * Client-side image compression for uploads.
 * Preserves aspect ratio (scale by longest side). Output is always JPEG data URL.
 */

export interface CompressionOptions {
  /** Max length in pixels for the longest side. Width/height scaled proportionally. */
  maxDimension: number;
  /** JPEG quality 0–1. */
  quality: number;
  /** If set, ensure output size <= this (bytes). Reduces quality/dimension in a loop until under. */
  maxSizeBytes?: number;
}

function estimateBase64DecodedBytes(dataUrl: string): number {
  const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
  if (!base64) return 0;
  return Math.floor((base64.length * 3) / 4);
}

function compressToDataUrl(
  file: File,
  maxDimension: number,
  quality: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('Ugyldig filtype. Kun bilder er tillatt.'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Kunne ikke behandle bildet. Prøv igjen.'));
          return;
        }

        let width = img.width;
        let height = img.height;
        const scale = Math.min(1, maxDimension / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => reject(new Error('Kunne ikke laste bildet. Prøv et annet bilde.'));
      img.src = dataUrl;
    };
    reader.onerror = () => reject(new Error('Kunne ikke lese filen. Prøv igjen.'));
    reader.readAsDataURL(file);
  });
}

const MAX_ITERATIONS = 6;
const QUALITY_STEPS = [0.85, 0.7, 0.55, 0.4, 0.3, 0.2];
const DIMENSION_STEPS = [1920, 1600, 1280, 1024, 800, 640];

/**
 * Compress an image file for upload. Preserves aspect ratio.
 * Returns a data URL (data:image/jpeg;base64,...) suitable for existing API bodies.
 */
export async function compressImageForUpload(
  file: File,
  options: CompressionOptions
): Promise<string> {
  const { maxDimension, quality, maxSizeBytes } = options;

  let result = await compressToDataUrl(file, maxDimension, quality);

  if (!maxSizeBytes || estimateBase64DecodedBytes(result) <= maxSizeBytes) {
    return result;
  }

  let currentDimension = maxDimension;
  for (let i = 0; i < MAX_ITERATIONS; i++) {
    const q = QUALITY_STEPS[Math.min(i, QUALITY_STEPS.length - 1)];
    const dim = DIMENSION_STEPS[Math.min(Math.floor(i / 2), DIMENSION_STEPS.length - 1)];
    currentDimension = Math.min(currentDimension, dim);
    result = await compressToDataUrl(file, currentDimension, q);
    if (estimateBase64DecodedBytes(result) <= maxSizeBytes) {
      return result;
    }
  }

  throw new Error('Bildet er for stort. Prøv et mindre bilde eller reduser oppløsningen.');
}

/** Preset for profile and showcase images: high quality, under Storage limit (avoids 413). */
export const PROFILE_SHOWCASE_PRESET: CompressionOptions = {
  maxDimension: 1920,
  quality: 0.85,
  maxSizeBytes: 2.5 * 1024 * 1024,
};

/** Preset for portfolio posts and talent package images. */
export const PORTFOLIO_PRESET: CompressionOptions = {
  maxDimension: 800,
  quality: 0.5,
};
